import React, { Suspense, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ERROR_CODES } from "@components/common/error";
import { MobileGate } from "@components/common/mobile-gate";
import { ORG_SEGMENT, PATHS } from "@constants/Paths";
import { AnalyticsArea, useActiveOrgRecovery, useBillingEnabled, useHomeDestination, usePageTracking, useTrackEvent, } from "@hooks";
import { ActiveOrgLayout, AppLayout, AuthLayout, BoxLayout, DashboardLayout, DataProviderLayout } from "@layouts";
import { PeekScreenLoader } from "@piiqtechnologies/ui-components";
import { usePolicyContext } from "@piiqtechnologies/ui-lib";
import { DiscoverAction } from "@constants/Policies";
import { useAuthContext } from "@stores/AuthContext";
import { getIsAdmin } from "@stores/utils";
/**
 * Renders children when billing is enabled, otherwise redirects home. Mounted both under
 * ActiveOrgLayout (the org-scoped report-type/checkout/new-report below) and, for staff, on the
 * unprefixed /new-report — where no ActiveOrganizationProvider exists — so the redirect target is
 * resolved with the same isAdmin / activeOrg / RESTRICTED fallback used by PlansBillingContext and
 * AccountSettings rather than a throwing hook.
 */
const BillingRoute = ({ children }) => {
    const billingEnabled = useBillingEnabled();
    const home = useHomeDestination();
    if (!billingEnabled)
        return React.createElement(Navigate, { to: home, replace: true });
    return children;
};
/**
 * Renders children when the user may manage billing in the organization on screen, otherwise sends
 * them to its home. Plans & Billing used to be an overlay only the sidebar could open, so the
 * sidebar's capability check was the whole gate; as a page it has a URL anyone can type, and
 * `getIsDiscoverAdmin` reads the account-wide role, not the role in THIS organization.
 */
const ManageBillingRoute = ({ children }) => {
    const canManageBilling = usePolicyContext(DiscoverAction.ManageBilling);
    const home = useHomeDestination();
    if (!canManageBilling)
        return React.createElement(Navigate, { to: home, replace: true });
    return children;
};
/** Renders children when the user may manage users, otherwise sends them to the org's home. */
const ManageUsersRoute = ({ children }) => {
    const canManageUsers = usePolicyContext(DiscoverAction.ManageUsers);
    const home = useHomeDestination();
    if (!canManageUsers)
        return React.createElement(Navigate, { to: home, replace: true });
    return children;
};
/**
 * Catch-all 404. A thin wrapper (not the shared Error component, which is also rendered as the
 * Sentry boundary fallback outside the analytics providers) so we can emit a Page Not Found event.
 */
const NotFound = () => {
    const track = useTrackEvent();
    const { pathname } = useLocation();
    const hasTrackedRef = useRef(false);
    useEffect(() => {
        if (hasTrackedRef.current)
            return;
        hasTrackedRef.current = true;
        track(AnalyticsArea.Error, "Page Not Found", { path: pathname });
    }, [track, pathname]);
    return React.createElement(Error, { errorCode: ERROR_CODES.PAGE_NOT_FOUND });
};
const Landing = React.lazy(() => import("@pages/landing"));
const EmbedSignup = React.lazy(() => import("@pages/embed-signup"));
const ReportType = React.lazy(() => import("@pages/report-type"));
const Checkout = React.lazy(() => import("@pages/checkout"));
const Results = React.lazy(() => import("@pages/results"));
const Login = React.lazy(() => import("@pages/login"));
const Demo = React.lazy(() => import("@pages/demo"));
const ForgotPassword = React.lazy(() => import("@pages/forgot-password"));
const ResetPassword = React.lazy(() => import("@pages/reset-password"));
const SSOLoginError = React.lazy(() => import("@pages/login-error"));
const InvalidToken = React.lazy(() => import("@pages/invalid-token"));
const Restricted = React.lazy(() => import("@pages/restricted"));
const SelectOrganization = React.lazy(() => import("@pages/select-organization"));
const AdminHome = React.lazy(() => import("@pages/admin-home"));
const Community = React.lazy(() => import("@pages/community"));
const Home = React.lazy(() => import("@pages/home"));
const NewReport = React.lazy(() => import("@pages/new-report"));
const Overview = React.lazy(() => import("@pages/overview"));
const ReportSettings = React.lazy(() => import("@pages/report-settings"));
const PlansBilling = React.lazy(() => import("@pages/plans-billing"));
const AccountSettings = React.lazy(() => import("@pages/account-settings"));
const TermsConditionsAgreement = React.lazy(() => import("@pages/terms-conditions-agreement"));
const Error = React.lazy(() => import("@pages/error"));
const AppRoutes = () => {
    const { user } = useAuthContext();
    const isAdmin = getIsAdmin(user);
    // Fire a page view on every route change across the whole app.
    usePageTracking();
    // Above every route, because any of them can be the one that discovers the active organization
    // has gone stale.
    useActiveOrgRecovery(isAdmin);
    return (React.createElement(Suspense, { fallback: React.createElement(PeekScreenLoader, { layered: true }) },
        React.createElement(MobileGate, null),
        React.createElement(Routes, null,
            React.createElement(Route, { path: PATHS.LANDING, element: React.createElement(Landing, null) }),
            React.createElement(Route, { path: PATHS.EMBED_SIGNUP, element: React.createElement(EmbedSignup, null) }),
            React.createElement(Route, { path: PATHS.RESULTS, element: React.createElement(Results, null) }),
            React.createElement(Route, { element: React.createElement(AppLayout, null) },
                React.createElement(Route, { element: React.createElement(BoxLayout, null) },
                    React.createElement(Route, { path: PATHS.LOGIN, element: React.createElement(Login, null) }),
                    React.createElement(Route, { path: PATHS.DEMO, element: React.createElement(Demo, null) }),
                    React.createElement(Route, { path: PATHS.FORGOT_PASSWORD, element: React.createElement(ForgotPassword, null) }),
                    React.createElement(Route, { path: PATHS.RESET_PASSWORD, element: React.createElement(ResetPassword, null) }),
                    React.createElement(Route, { path: PATHS.LOGIN_ERROR, element: React.createElement(SSOLoginError, null) }),
                    React.createElement(Route, { path: PATHS.INVALID_TOKEN, element: React.createElement(InvalidToken, null) }),
                    React.createElement(Route, { path: PATHS.RESTRICTED, element: React.createElement(Restricted, null) }),
                    React.createElement(Route, { path: PATHS.TERMS_CONDITIONS_AGREEMENT, element: React.createElement(TermsConditionsAgreement, null) }),
                    React.createElement(Route, { path: "*", element: React.createElement(NotFound, null) })),
                React.createElement(Route, { element: React.createElement(AuthLayout, null) },
                    React.createElement(Route, { path: ORG_SEGMENT, element: React.createElement(ActiveOrgLayout, null) },
                        React.createElement(Route, { path: "report-type", element: React.createElement(BillingRoute, null,
                                React.createElement(ReportType, null)) }),
                        React.createElement(Route, { path: "checkout", element: React.createElement(BillingRoute, null,
                                React.createElement(Checkout, null)) }),
                        React.createElement(Route, { path: "new-report", element: React.createElement(BillingRoute, null,
                                React.createElement(NewReport, null)) }),
                        React.createElement(Route, { element: React.createElement(DashboardLayout, null) },
                            React.createElement(Route, { element: React.createElement(DataProviderLayout, null) },
                                React.createElement(Route, { path: "home", element: React.createElement(Home, null) }),
                                React.createElement(Route, { path: "report/:reportId", element: React.createElement(ReportSettings, null) }),
                                React.createElement(Route, { path: "report/:reportId/settings/topics-and-subtopics", element: React.createElement(ReportSettings, null) }),
                                React.createElement(Route, { path: "report/:reportId/settings/tracked-competitors", element: React.createElement(ReportSettings, null) })),
                            React.createElement(Route, { path: "overview/:assessmentId", element: React.createElement(Overview, null) }),
                            React.createElement(Route, { path: "community/:communityId", element: React.createElement(Community, null) }),
                            React.createElement(Route, { path: "results/:assessmentId", element: React.createElement(Results, null) }),
                            React.createElement(Route, { path: "plans-and-billing", element: React.createElement(BillingRoute, null,
                                    React.createElement(ManageBillingRoute, null,
                                        React.createElement(PlansBilling, null))) }),
                            React.createElement(Route, { path: "account-settings", element: React.createElement(ManageUsersRoute, null,
                                    React.createElement(AccountSettings, null)) }))),
                    React.createElement(Route, { element: React.createElement(BoxLayout, null) },
                        React.createElement(Route, { path: PATHS.SELECT_ORGANIZATION, element: React.createElement(SelectOrganization, null) })),
                    isAdmin && React.createElement(Route, { path: PATHS.ADMIN_DASHBOARD, element: React.createElement(AdminHome, null) }),
                    React.createElement(Route, { path: PATHS.HOME, element: React.createElement(Restricted, null) }),
                    React.createElement(Route, { path: PATHS.REPORT_TYPE, element: React.createElement(Restricted, null) }),
                    React.createElement(Route, { path: PATHS.CHECKOUT, element: React.createElement(Restricted, null) }),
                    React.createElement(Route, { element: isAdmin ? React.createElement(DashboardLayout, null) : undefined },
                        React.createElement(Route, { path: PATHS.OVERVIEW, element: isAdmin ? React.createElement(Overview, null) : React.createElement(Restricted, null) }),
                        React.createElement(Route, { path: PATHS.COMMUNITY, element: isAdmin ? React.createElement(Community, null) : React.createElement(Restricted, null) }),
                        React.createElement(Route, { path: PATHS.PLANS_BILLING, element: isAdmin ? (React.createElement(BillingRoute, null,
                                React.createElement(PlansBilling, null))) : (React.createElement(Restricted, null)) }),
                        React.createElement(Route, { path: PATHS.ACCOUNT_SETTINGS, element: isAdmin ? React.createElement(AccountSettings, null) : React.createElement(Restricted, null) }),
                        isAdmin ? (React.createElement(Route, { element: React.createElement(DataProviderLayout, null) },
                            React.createElement(Route, { path: PATHS.REPORT_SETTINGS, element: React.createElement(ReportSettings, null) }),
                            React.createElement(Route, { path: PATHS.REPORT_SETTINGS_TOPICS, element: React.createElement(ReportSettings, null) }),
                            React.createElement(Route, { path: PATHS.REPORT_SETTINGS_COMPETITORS, element: React.createElement(ReportSettings, null) }))) : (React.createElement(Route, { path: PATHS.REPORT_SETTINGS, element: React.createElement(Restricted, null) }))),
                    React.createElement(Route, { path: PATHS.NEW_REPORT, element: isAdmin ? (React.createElement(BillingRoute, null,
                            React.createElement(NewReport, null))) : (React.createElement(Restricted, null)) }))))));
};
export default AppRoutes;
